import { memo, useId } from 'react';

import {
  CANVAS,
  CELL,
  DROP_DELAYS_MS,
  DROP_DURATION_MS,
  FLOAT_PERIODS_S,
  TILE,
  PulsePlane,
  TILES,
  wavyTile,
} from './pulse-block-tiles';

/**
 * The welcome screen's brand moment. Pure inline vector, decorative to
 * assistive tech exactly like the logo SVGs, and every motion is CSS - so no
 * render is needed to keep any of it moving.
 *
 * Memoized because its only prop is a constant: without it the whole grid
 * reconciles every time the welcome screen re-renders, which it does on each
 * contact-sheet open and close.
 *
 * The root carries `overflow-visible`: the flight's radius takes the plane
 * past the viewBox edges, and an SVG clips to its box by default - which
 * sliced the plane in half every time it passed a corner.
 *
 * Three motions, layered so none fights another for the same `transform`:
 * the OUTER group falls (`pp-block-drop`), the INNER group breathes
 * (`pp-block-float`), and a sheen sweeps across the paper on a long loop.
 * Two animations on one element would overwrite each other's transform, which
 * is the whole reason for the nesting.
 */
function PulseBlocksArtwork({ className }: Readonly<{ className?: string }>) {
  // Scoped so a second instance cannot capture this one's gradient and clip.
  const uid = useId().replaceAll(':', '');
  const sheenGradientId = `pp-sheen-${uid}`;
  const paperClipId = `pp-paper-${uid}`;

  const placements = TILES.map((tile) => {
    const x = tile.col * CELL;
    const y = tile.row * CELL;
    return {
      tile,
      x,
      y,
      transform: `rotate(${tile.tilt} ${x + TILE / 2} ${y + TILE / 2}) translate(${x} ${y})`,
    };
  });

  return (
    <svg
      aria-hidden="true"
      className={`overflow-visible ${className ?? ''}`}
      data-testid="pulse-blocks"
      fill="none"
      viewBox={`0 0 ${CANVAS} ${CANVAS}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={sheenGradientId} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
        {/* The sheen is light catching PAPER, so it is clipped to the tiles
            rather than washing the canvas between them. The clip is static
            while the tiles breathe; the drift is under a pixel on screen. */}
        <clipPath id={paperClipId}>
          {placements.map(({ tile, transform }) => (
            <path key={`${tile.col}-${tile.row}`} d={wavyTile(tile.edges)} transform={transform} />
          ))}
        </clipPath>
      </defs>

      {placements.map(({ tile, transform }, index) => (
        <g
          key={`${tile.col}-${tile.row}`}
          className="pp-block-drop"
          style={{ animationDelay: `${DROP_DELAYS_MS[index]}ms` }}
        >
          <g
            className="pp-block-float"
            style={{
              animationDuration: `${FLOAT_PERIODS_S[index]}s`,
              // Breathing starts only once THIS tile has seated, so the fall
              // reads as gravity rather than as a tile that never settled.
              animationDelay: `${DROP_DELAYS_MS[index] + DROP_DURATION_MS}ms`,
            }}
          >
            <g transform={transform}>
              <path d={wavyTile(tile.edges)} fill={tile.fill} />
              {tile.glyph}
            </g>
          </g>
        </g>
      ))}

      {/* Above the tiles and OUTSIDE the paper clip: it flies over the grid,
          it is not light caught on it. */}
      <PulsePlane />

      <g className="pp-blocks-sheen" clipPath={`url(#${paperClipId})`}>
        <rect
          fill={`url(#${sheenGradientId})`}
          height={CANVAS * 1.8}
          transform={`rotate(18 ${CANVAS / 2} ${CANVAS / 2})`}
          width={CANVAS * 0.3}
          x={-CANVAS * 0.35}
          y={-CANVAS * 0.4}
        />
      </g>
    </svg>
  );
}

export const PulseBlocks = memo(PulseBlocksArtwork);
